import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { type AddressSuggestion, createAddressSession } from '../places';

interface AddressInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
}

/** Long enough that the first request is worth its money. */
const MIN_QUERY_LENGTH = 4;
const DEBOUNCE_MS = 300;

/**
 * An address field: free text, with Google's suggestions underneath. Picking one
 * stores the address as Google spells it, so the server geocodes something clean.
 * Without a key — or with Places unavailable — it is exactly a plain input.
 */
export function AddressInput({
  value,
  onChange,
  placeholder,
  required,
  autoFocus,
}: AddressInputProps) {
  const session = useMemo(() => createAddressSession(), []);
  const listId = useId();
  // Null while nothing is being composed: right after a pick, or before she types.
  const [query, setQuery] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dismissed, setDismissed] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const list = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (query === null) return;
    const text = query.trim();
    if (text.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      return;
    }

    let active = true;
    const timer = setTimeout(() => {
      void session.suggest(text).then((found) => {
        if (!active) return;
        setSuggestions(found);
        setActiveIndex(-1);
      });
    }, DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, session]);

  const open = !dismissed && suggestions.length > 0;

  // On a phone the field can sit at the bottom of the screen, the list below the
  // fold; `scroll-margin-bottom` keeps it clear of the navigation bar.
  useEffect(() => {
    if (open) list.current?.scrollIntoView({ block: 'nearest' });
  }, [open, suggestions]);

  useEffect(() => {
    if (activeIndex >= 0) {
      document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, listId]);

  function select(suggestion: AddressSuggestion) {
    onChange(suggestion.address);
    session.reset();
    setQuery(null);
    setSuggestions([]);
    setActiveIndex(-1);
    input.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setDismissed(true);
      return;
    }
    if (!open) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? suggestions.length : index) - 1);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      // Otherwise Enter would submit the form with the half-typed address.
      event.preventDefault();
      select(suggestions[activeIndex] as AddressSuggestion);
    }
  }

  return (
    <div className="address">
      <input
        ref={input}
        className="input"
        type="text"
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setQuery(event.target.value);
          setDismissed(false);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setDismissed(true)}
      />

      {open && (
        <ul className="address__list" id={listId} role="listbox" ref={list}>
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.address}>
              <button
                type="button"
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={
                  index === activeIndex ? 'address__option is-active' : 'address__option'
                }
                // Keeps the focus on the input, so the blur never beats the click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => select(suggestion)}
              >
                <span className="address__main">{suggestion.main}</span>
                {suggestion.secondary && (
                  <span className="address__secondary">{suggestion.secondary}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
